import * as React from 'react';
import { Helmet } from 'react-helmet';
import { Fabric, PrimaryButton } from '@fluentui/react';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { useSelector } from 'react-redux';
import { SelectOption } from 'formComponents/Select/types';
import { lookupsLoadingFinishedSelector } from 'store/lookups/selector';
import useDebouncedState from 'hooks/useDebouncedState';
import { Select } from 'formComponents/Select/Select';
import { Label } from '@fluentui/react/lib/Label';
import moment from 'moment-timezone';
import range from 'lodash/range';
import { generateReportPrintUrl } from 'reports/utils';
import { Arbeitszeituebersicht } from './Arbeitszeituebersicht';

const styles = mergeStyleSets({
    container: {
        position: 'relative',
        height: 'calc(100vh - 75px)',
        display: 'grid',
        gridTemplateRows: 'max-content 1fr',
        gridRowGap: 10,
    },
    searchBar: {
        display: 'grid',
        gridTemplateColumns: '160px 160px 1fr max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
    actionButtons: {
        marginTop: 29,
        display: 'grid',
        gridTemplateColumns: 'repeat(2, max-content)',
        gridColumnGap: 10,
    },
});

interface FilterValues {
    workYear: number;
    workMonth: number;
}

const defaultFilterValues: FilterValues = {
    workYear: moment().year(),
    workMonth: moment().month() + 1,
};

const yearOptions = range(moment().year(), moment().year() - 25).map((year) => ({
    value: year.toString(),
    label: year.toString(),
}));

const monthOptions = range(0, 12).map((month) => ({
    value: (month + 1).toString(),
    label: (month + 1).toString(),
}));

export const ArbeitszeituebersichtView = (): JSX.Element => {
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);

    const [filterValues, debouncedFilterValues, setFilterValues] = useDebouncedState<FilterValues>(() => {
        return {
            ...defaultFilterValues,
        };
    }, 1000);

    const selectedYearOption = React.useMemo<SelectOption>(
        () => ({ value: filterValues.workYear.toString(), label: filterValues.workYear.toString() }),
        [filterValues.workYear],
    );

    const selectedMonthOption = React.useMemo<SelectOption>(
        () => ({ value: filterValues.workMonth.toString(), label: filterValues.workMonth.toString() }),
        [filterValues.workMonth],
    );

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    return (
        <Fabric className={styles.container}>
            <Helmet>
                <title>Arbeitszeit</title>
            </Helmet>
            <div className={styles.searchBar}>
                <div>
                    <Label>Jahr</Label>
                    <Select
                        name="Jahr"
                        options={yearOptions}
                        value={selectedYearOption}
                        onChange={(newOption) => {
                            setFilterValues({
                                ...filterValues,
                                workYear: parseInt(newOption ? newOption.value : moment().year().toString(), 10),
                            });
                        }}
                    />
                </div>
                <div>
                    <Label>Monat</Label>
                    <Select
                        name="Monat"
                        options={monthOptions}
                        value={selectedMonthOption}
                        onChange={(newOption) => {
                            setFilterValues({
                                ...filterValues,
                                workMonth: parseInt(
                                    newOption ? newOption.value : (moment().month() + 1).toString(),
                                    10,
                                ),
                            });
                        }}
                    />
                </div>

                <div />

                <div className={styles.actionButtons}>
                    <PrimaryButton
                        onClick={() => {
                            window.open(
                                generateReportPrintUrl('arbeitszeituebersicht', {
                                    workYear: filterValues.workYear,
                                    workMonth: filterValues.workMonth,
                                }),
                            );
                        }}
                        text="Drucken"
                    />
                </div>
            </div>

            <Arbeitszeituebersicht
                workYear={debouncedFilterValues.workYear}
                workMonth={debouncedFilterValues.workMonth}
            />
        </Fabric>
    );
};
